/**
 * Cliente das rotas do futebol brasileiro para a TELA do painel.
 *
 * Só rotas com a sessão do operador (JWT, que o cliente `api` anexa): leitura em
 * `/futebol/*` e operação da coleta em `/config/futebol/*`. Nunca a API de dados
 * (`/dados/*`): aquela é do consumidor externo, com token de servidor próprio, e
 * esse token não pode passar por um navegador.
 *
 * 403 (recurso fora do plano), 404 (não existe) e 503 (ainda não coletado) são
 * estados que a tela explica com o motivo do servidor; por isso não viram alarme
 * global.
 */
import { api, apiFetchRaw } from '@/services/api';
import type {
  Atleta,
  AtletaBusca,
  Classificacao,
  Clube,
  EnvelopeFutebol,
  EstadoColeta,
  FutebolAcesso,
  JogoFicha,
  Rodada,
  Rodadas,
  SerieCbf,
} from '@/types/futebol';

export const STATUS_DA_TELA = [403, 404, 503] as const;
const OPCOES = { esperados: STATUS_DA_TELA };

export type TipoImagem = 'clube' | 'atleta';

function comFase(caminho: string, fase?: string | null): string {
  return fase ? `${caminho}?fase=${encodeURIComponent(fase)}` : caminho;
}

export const futebolApi = {
  acesso: () => api.get<FutebolAcesso>('/futebol/acesso', OPCOES),

  serie: (serie: SerieCbf) =>
    api.get<EnvelopeFutebol<Classificacao>>(`/futebol/brasileirao/${serie}`, OPCOES),

  rodadas: (serie: SerieCbf, fase?: string | null) =>
    api.get<EnvelopeFutebol<Rodadas>>(
      comFase(`/futebol/brasileirao/${serie}/rodadas`, fase),
      OPCOES,
    ),

  rodada: (serie: SerieCbf, n: number, fase?: string | null) =>
    api.get<EnvelopeFutebol<Rodada>>(
      comFase(`/futebol/brasileirao/${serie}/rodadas/${n}`, fase),
      OPCOES,
    ),

  jogo: (id: number) => api.get<EnvelopeFutebol<JogoFicha>>(`/futebol/jogos/${id}`, OPCOES),

  clube: (id: number) => api.get<EnvelopeFutebol<Clube>>(`/futebol/clubes/${id}`, OPCOES),

  buscarAtletas: (q: string) =>
    api.get<EnvelopeFutebol<AtletaBusca>>(`/futebol/atletas?q=${encodeURIComponent(q)}`, OPCOES),

  atleta: (id: number) => api.get<EnvelopeFutebol<Atleta>>(`/futebol/atletas/${id}`, OPCOES),

  /** Escudo e foto exigem o JWT, então vêm por fetch e viram objeto local na tela. */
  imagem: async (tipo: TipoImagem, id: number): Promise<Blob> => {
    const caminho =
      tipo === 'clube' ? `/futebol/imagens/clubes/${id}.png` : `/futebol/imagens/atletas/${id}.jpg`;
    const resposta = await apiFetchRaw(caminho, { headers: { Accept: 'image/*' } }, OPCOES);
    return resposta.blob();
  },

  /** Operação da coleta: só staff (leitura do estado). */
  estado: () => api.get<EstadoColeta>('/config/futebol/estado', OPCOES),

  /** Só staff com papel admin. `ligado = true` PARA a coleta. */
  killSwitch: (ligado: boolean, motivo?: string) =>
    api.put<EstadoColeta['kill_switch']>(
      '/config/futebol/kill-switch',
      { ligado, motivo: motivo?.trim() ? motivo.trim() : null },
      OPCOES,
    ),

  /** Só staff com papel admin. Marca tudo como vencido; o laço refaz no ritmo da fonte. */
  varredura: () =>
    api.post<{ pedida_em: string; por: string }>('/config/futebol/varredura', undefined, OPCOES),
};
